import React, { createContext, useContext, useState, useCallback } from 'react';
import { EditMode, EditingContext } from './types';

const SelectionContext = createContext<EditingContext | null>(null);

export const useSelection = () => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within SelectionProvider');
  }
  return context;
};

interface SelectionProviderProps {
  children: React.ReactNode;
  initialMode?: EditMode;
  onSelectionChange?: (selectedIds: string[]) => void;
  onDelete?: (ids: string[]) => void;
}

export const SelectionProvider: React.FC<SelectionProviderProps> = ({
  children,
  initialMode = 'select',
  onSelectionChange,
  onDelete,
}) => {
  const [mode, setMode] = useState<EditMode>(initialMode);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const selectElement = useCallback((id: string, multiSelect = false) => {
    setSelectedIds((prev) => {
      let newSelection: string[];
      
      if (multiSelect) {
        // Toggle selection in multi-select mode
        if (prev.includes(id)) {
          newSelection = prev.filter((selectedId) => selectedId !== id);
        } else {
          newSelection = [...prev, id];
        }
      } else {
        // Single select - replace selection
        newSelection = [id];
      }
      
      onSelectionChange?.(newSelection);
      return newSelection;
    });
  }, [onSelectionChange]);

  const selectElements = useCallback((ids: string[], replace = true) => {
    setSelectedIds((prev) => {
      const newSelection = replace ? ids : [...prev, ...ids.filter(id => !prev.includes(id))];
      onSelectionChange?.(newSelection);
      return newSelection;
    });
  }, [onSelectionChange]);

  const deselectElement = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSelection = prev.filter((selectedId) => selectedId !== id);
      onSelectionChange?.(newSelection);
      return newSelection;
    });
  }, [onSelectionChange]);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    onSelectionChange?.([]);
  }, [onSelectionChange]);

  const deleteSelected = useCallback(() => {
    if (selectedIds.length > 0) {
      onDelete?.(selectedIds);
      clearSelection();
    }
  }, [selectedIds, onDelete, clearSelection]);

  const isSelected = useCallback((id: string) => {
    return selectedIds.includes(id);
  }, [selectedIds]);

  const value: EditingContext = {
    mode,
    selectedIds,
    selectElement,
    deselectElement,
    clearSelection,
    deleteSelected,
    isSelected,
    selectElements, // Add batch selection function
  };

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
};
